import React from 'react';
import {
  Spinner,
  Button,
  Skeleton,
  CardSkeleton,
  ListSkeleton,
  TableSkeleton,
} from '../ui';

// Page-level loading indicator with optional message and skeleton support
interface PageLoadingProps {
  message?: string;
  variant?: 'spinner' | 'skeleton';
  skeletonLines?: number;
}

export const PageLoading: React.FC<PageLoadingProps> = ({
  message = 'Loading...',
  variant = 'spinner',
  skeletonLines = 5,
}) => {
  if (variant === 'skeleton') {
    return (
      <div className="w-full p-6">
        {Array.from({ length: skeletonLines }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            height={index === 0 ? '2.5rem' : '1.25rem'}
            className={index === 0 ? 'mb-4' : 'mb-2'}
            width={index === 0 ? '60%' : `${Math.random() * 40 + 60}%`}
          />
        ))}
        <div className="mt-6">
          <Skeleton variant="rectangular" height="120px" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-8">
      <Spinner size="lg" className="text-primary-600" />
      <p className="mt-4 text-secondary-600">{message}</p>
    </div>
  );
};

// Loading button with consistent styling and spinner
interface LoadingButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  loadingText,
  children,
  disabled,
  leftIcon,
  variant = 'primary',
  size = 'md',
  ...buttonProps
}) => {
  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled || loading}
      loading={loading}
      loadingText={loadingText}
      leftIcon={!loading ? leftIcon : undefined}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

// Inline loading indicator for content areas
interface InlineLoadingProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const InlineLoading: React.FC<InlineLoadingProps> = ({
  message = 'Loading...',
  size = 'md',
}) => (
  <div className="flex items-center justify-center gap-2 py-4">
    <Spinner size={size} className="text-primary-600" />
    <span className="text-sm text-secondary-600">{message}</span>
  </div>
);

// Overlay loading for specific content areas
interface LoadingOverlayProps {
  loading: boolean;
  children: React.ReactNode;
  message?: string;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  loading,
  children,
  message = 'Loading...',
}) => (
  <div className="relative">
    {children}
    {loading && (
      <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-50">
        <Spinner size="lg" className="text-primary-600" />
        <span className="mt-2 text-sm text-secondary-600">{message}</span>
      </div>
    )}
  </div>
);

// Re-export skeleton components for convenience
export { CardSkeleton, ListSkeleton, TableSkeleton };
